import { z } from 'zod';
import { optionalMongoObjectId } from './object-id-helpers.js';
import { TEAM_TYPE_VALUES } from './team-association.registry.js';

function pushUniqueId(ids, seen, value) {
    const id = value == null || value === ''
        ? ''
        : String(value._id || value.id || value).trim();
    if (!id || id === '[object Object]' || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
}

export function collectTeamLeaderIds(team = {}) {
    const seen = new Set();
    const ids = [];
    if (Array.isArray(team.leaders)) {
        for (const value of team.leaders) pushUniqueId(ids, seen, value);
    }
    pushUniqueId(ids, seen, team.leader);
    return ids;
}

export function collectTeamPoolUserIds(team = {}) {
    const seen = new Set();
    const ids = [];
    for (const value of collectTeamLeaderIds(team)) pushUniqueId(ids, seen, value);
    if (Array.isArray(team.members)) {
        for (const value of team.members) pushUniqueId(ids, seen, value);
    }
    return ids;
}

function withNormalizedLeaders(data) {
    const hasLeaderInput = data.leaders != null || data.leader != null;
    if (!hasLeaderInput) return data;
    const leaders = collectTeamLeaderIds(data);
    return { ...data, leaders, leader: leaders[0] };
}

export const TeamSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    name: z.string().min(1),
    code: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    type: z.enum(TEAM_TYPE_VALUES).optional().default('other'),
    email: z.union([z.string().email(), z.literal('')]).optional().nullable(),
    leader: z.string().optional(),
    leaders: z.array(z.string()).optional(),
    members: z.array(z.string()).optional(),
    unit: optionalMongoObjectId,
    status: z.enum(['active', 'inactive']).optional().default('active'),
    customFields: z.record(z.unknown()).optional(),
    createdAt: z.union([z.string(), z.date()]).optional(),
    updatedAt: z.union([z.string(), z.date()]).optional(),
});

export const CreateTeamSchema = TeamSchema
    .omit({ id: true, createdAt: true, updatedAt: true })
    .transform(withNormalizedLeaders)
    .superRefine((value, ctx) => {
        if (!collectTeamLeaderIds(value).length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['leaders'],
                message: 'At least one team leader is required',
            });
        }
    });
export const UpdateTeamSchema = TeamSchema.transform(withNormalizedLeaders);
export const TeamFormSchema = TeamSchema;
